import { validQuestionItemIds } from './questionIds';
import { questionDefaults } from './questionDefaults';
import { validTextFormat } from './richText';
import { validationConfigError, validationShape } from './questionSettings';
import type { EventInput, Field, FieldType } from './types';
import { isValidPhone, PHONE_ERROR } from './phone';
import { supportsRouting } from './questionRouting';

const FIELD_TYPES: FieldType[] = ['short_text','paragraph','choice','checkboxes','dropdown','linear_scale','rating','choice_grid','checkbox_grid','date','time','acknowledgment','section','title_text'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const MAX_WARD_NAMES = 60;

/** Trim, drop blanks, and remove case-insensitive duplicates, keeping the first spelling. */
export function cleanWardNames(names: string[] | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names ?? []) {
    const w = String(raw).trim().replace(/\s+/g, ' ');
    const k = w.toLowerCase();
    if (!w || seen.has(k)) continue;
    seen.add(k);
    out.push(w);
  }
  return out;
}

export function isValidDate(s: string): boolean {
  if (!DATE_RE.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** The longest short name a section or form can have in the registration steps. */
export const SHORT_NAME_MAX = 40;
export function validateFields(fields: unknown): string[] {
  const errors: string[] = [];
  if (!Array.isArray(fields)) return ['fields must be a list'];
  // Anything the organizer leaves empty uses its default, such as “Untitled question” or “Option 1”.
  const questions = fields.map(questionDefaults) as Field[];
  const ids = new Set<string>();
  questions.forEach((f: Field, i) => {
    const at = `question ${i + 1}`;
    if (!f || typeof f !== 'object') { errors.push(`${at}: invalid`); return; }
    if (typeof f.id !== 'string' || !f.id) errors.push(`${at}: missing id`);
    else if (ids.has(f.id)) errors.push(`${at}: duplicate id`);
    else ids.add(f.id);
    if (!validQuestionItemIds(f.optionIds, f.options?.length ?? 0)) errors.push(`${at}: invalid option identities`);
    if (f.grid && (!validQuestionItemIds(f.grid.rowIds, f.grid.rows?.length ?? 0) || !validQuestionItemIds(f.grid.columnIds, f.grid.columns?.length ?? 0))) errors.push(`${at}: invalid grid identities`);
    if (!FIELD_TYPES.includes(f.type)) errors.push(`${at}: unknown type`);
    if (f.descriptionEnabled !== undefined && typeof f.descriptionEnabled !== 'boolean') errors.push(`${at}: invalid description setting`);
    for (const key of ['shuffle', 'validationEnabled', 'limitOnePerColumn', 'shuffleRows', 'includeTime', 'includeYear'] as const) if (f[key] !== undefined && typeof f[key] !== 'boolean') errors.push(`${at}: invalid ${key} setting`);
    if (f.timeMode !== undefined && !['time', 'duration'].includes(f.timeMode)) errors.push(`${at}: invalid time answer type`);
    if (f.validation !== undefined && !validationShape(f.validation)) errors.push(`${at}: invalid response validation`);
    else { const validationError = validationConfigError(f); if (validationError) errors.push(`${at}: ${validationError}`); }
    if (f.sectionDestination !== undefined && (f.type !== 'section' || !validDestination(f.sectionDestination))) errors.push(`${at}: invalid section destination`);
    if (f.shortName !== undefined && (f.type !== 'section' || typeof f.shortName !== 'string' || f.shortName.length > SHORT_NAME_MAX)) errors.push(`${at}: short name must be ${SHORT_NAME_MAX} characters or fewer`);
    if (f.routing !== undefined && !validRouting(f.routing)) errors.push(`${at}: invalid answer routing`);
    if (!validTextFormat(f.labelFormat, typeof f.label === 'string' ? f.label : '') || !validTextFormat(f.helpFormat, typeof f.help === 'string' ? f.help : '')) errors.push(`${at}: invalid text formatting`);
    if (typeof f.label !== 'string') errors.push(`${at}: invalid title`);
    if (f.type === 'acknowledgment' && f.acknowledgmentLabel !== undefined && (typeof f.acknowledgmentLabel !== 'string' || !f.acknowledgmentLabel.trim() || f.acknowledgmentLabel.length > 500)) errors.push(`${at}: checkbox label must be 1–500 characters`);
    if (f.type === 'acknowledgment' && f.required !== true) errors.push(`${at}: acknowledgment must be required`);
    if (['choice','checkboxes','dropdown'].includes(f.type)) {
      const opts = (f.options ?? []).map((o) => String(o).trim());
      // The editor reverts an option that repeats another, so this only guards stored data.
      if (new Set(opts.map(option => option.toLowerCase())).size !== opts.length) errors.push(`${at}: duplicate options not supported; use different option labels`);
    }
    if ((f.type === 'choice_grid' || f.type === 'checkbox_grid') && f.grid) {
      // The editor reverts a row or column that repeats another, so this only guards stored data.
      const repeated = (labels: string[] = []) => new Set(labels.map(label => String(label).trim().toLowerCase())).size !== labels.length;
      if (repeated(f.grid.rows) || repeated(f.grid.columns)) errors.push(`${at}: duplicate grid rows or columns not supported; use different labels`);
    }
    if (f.type === 'rating' && !validRating(f.rating)) errors.push(`${at}: choose 3–10 rating stars`);
    if (f.type === 'linear_scale') {
      const s = f.scale;
      if (!s || ![0, 1].includes(s.min) || !(s.max >= 2 && s.max <= 10)) errors.push(`${at}: scale must be 0/1 to 2–10`);
    }
  });
  // A section or answer that goes to a missing or earlier section continues to the next one, and when several questions
  // in a section route by answer, the last one answered decides; see questionPath.
  for (const f of questions) {
    if (!f || typeof f !== 'object' || !f.routing?.enabled) continue;
    const at = `question ${questions.indexOf(f) + 1}`;
    if (!validRouting(f.routing) || !supportsRouting(f)) errors.push(`${at}: answer routing is available for Multiple Choice and Dropdown questions`);
  }
  return errors;
}

export function validateEventInput(input: Partial<EventInput>): string[] {
  const errors: string[] = [];
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  if (input.level !== 'stake' && input.level !== 'ward') errors.push('Choose Stake or Ward');
  if (!str(input.stakeName)) errors.push('Stake name is required');
  if (input.level === 'ward' && !str(input.wardName)) errors.push('Ward name is required for a ward activity');
  if (input.wardNames !== undefined) {
    if (!Array.isArray(input.wardNames) || input.wardNames.some((w) => typeof w !== 'string')) errors.push('Ward names must be a list');
    else {
      if (input.wardNames.length > MAX_WARD_NAMES) errors.push(`At most ${MAX_WARD_NAMES} ward names`);
      if (input.wardNames.some((w) => w.trim().length > 80)) errors.push('Each ward name must be 80 characters or fewer');
    }
  }
  if (!str(input.name)) errors.push('Event name is required');
  if (str(input.name).length > 120) errors.push('Event name is too long');
  if (!Array.isArray(input.dates) || !input.dates.length) errors.push('Add at least one date');
  else {
    for (const d of input.dates) {
      if (!d || !isValidDate(d.start)) { errors.push('Each date must be a valid date'); break; }
      if (d.end && (!isValidDate(d.end) || d.end < d.start)) { errors.push('End date must be on or after the start date'); break; }
    }
  }
  if (str(input.description).length > 4000) errors.push('Description is too long');
  if (!str(input.leaderName)) errors.push('Event organizer name is required');
  if (!str(input.leaderPhone)) errors.push('Event organizer phone number is required');
  else if (!isValidPhone(str(input.leaderPhone))) errors.push('Event organizer phone number is invalid');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str(input.leaderEmail))) errors.push('Event organizer email is required');
  if (input.level === 'stake' && str(input.wardEntry)) errors.push('Add the ward to your list, or clear the entry.');
  if (input.adultAge !== undefined && ![18, 19, 21].includes(input.adultAge)) errors.push('Choose 18, 19 or 21 for the parent signature age');
  errors.push(...validateFields(input.fields ?? []));
  return errors;
}

/** Drafts may be incomplete, but their structure must be safe to render. */
export function validateDraftShape(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return ['Invalid draft'];
  const v = value as Record<string, unknown>;
  const text = (x: unknown) => typeof x === 'string' && x.length <= 10000;
  const list = (x: unknown) => Array.isArray(x) && x.length <= 200 && x.every(text);
  if (!['ward', 'stake'].includes(String(v.level))) return ['Invalid activity type'];
  if (!['stakeName', 'name', 'description', 'leaderName', 'leaderPhone', 'leaderEmail'].every((k) => text(v[k]))) return ['Invalid details'];
  if (v.wardName != null && !text(v.wardName)) return ['Invalid ward'];
  if (v.wardEntry !== undefined && !text(v.wardEntry)) return ['Invalid ward entry'];
  if (v.wardNames !== undefined && !list(v.wardNames)) return ['Invalid wards'];
  if (v.adultAge !== undefined && ![18, 19, 21].includes(v.adultAge as number)) return ['Invalid parent signature age'];
  if (!Array.isArray(v.dates) || v.dates.length > 50 || !v.dates.every((d) => d && typeof d === 'object' && text(d.start) && (d.end === undefined || text(d.end)))) return ['Invalid dates'];
  if (!Array.isArray(v.fields) || v.fields.length > 100) return ['Invalid questions'];
  for (const f of v.fields) { const error = fieldShapeError(f); if (error) return [error]; }
  return [];
}

/** Whether one stored question is safe to render, as in a draft: incomplete wording is fine. */
export function fieldShapeError(value: unknown): string | null {
  const text = (x: unknown) => typeof x === 'string' && x.length <= 10000;
  const list = (x: unknown) => Array.isArray(x) && x.length <= 200 && x.every(text);
  const f = value as Field & Record<string, unknown>;
  if (!f || typeof f !== 'object' || !text(f.id) || !f.id || !FIELD_TYPES.includes(f.type) || !text(f.label) || typeof f.required !== 'boolean') return 'Invalid question';
  if (f.acknowledgmentLabel !== undefined && (typeof f.acknowledgmentLabel !== 'string' || f.acknowledgmentLabel.length > 500)) return 'Invalid checkbox label';
  if (f.descriptionEnabled !== undefined && typeof f.descriptionEnabled !== 'boolean') return 'Invalid description setting';
  for (const key of ['shuffle', 'validationEnabled', 'limitOnePerColumn', 'shuffleRows', 'includeTime', 'includeYear'] as const) if (f[key] !== undefined && typeof f[key] !== 'boolean') return `Invalid ${key} setting`;
  if (f.timeMode !== undefined && !['time', 'duration'].includes(f.timeMode)) return 'Invalid time answer type';
  if (f.sectionDestination !== undefined && (f.type !== 'section' || !validDestination(f.sectionDestination))) return 'Invalid section destination';
  if (f.shortName !== undefined && (f.type !== 'section' || typeof f.shortName !== 'string' || f.shortName.length > SHORT_NAME_MAX)) return 'Invalid short name';
  if (f.routing !== undefined && !validRouting(f.routing)) return 'Invalid answer routing';
  if (f.help !== undefined && !text(f.help)) return 'Invalid help text';
  if (!validTextFormat(f.labelFormat, f.label) || !validTextFormat(f.helpFormat, f.help ?? '')) return 'Invalid text formatting';
  if (f.options !== undefined && !list(f.options)) return 'Invalid options';
  if (!validQuestionItemIds(f.optionIds, f.options?.length ?? 0)) return 'Invalid option identities';
  if (f.grid && (!validQuestionItemIds(f.grid.rowIds, f.grid.rows?.length ?? 0) || !validQuestionItemIds(f.grid.columnIds, f.grid.columns?.length ?? 0))) return 'Invalid grid identities';
  if (f.scale !== undefined && (!f.scale || ![0, 1].includes(f.scale.min) || !Number.isInteger(f.scale.max) || f.scale.max < 2 || f.scale.max > 10)) return 'Invalid scale';
  if ((f.type === 'rating' || f.rating !== undefined) && !validRating(f.rating)) return 'Invalid rating';
  if (f.grid !== undefined && (!f.grid || !list(f.grid.rows) || !list(f.grid.columns))) return 'Invalid grid';
  if (f.validation !== undefined && !validationShape(f.validation)) return 'Invalid response validation';
  return null;
}

/** What an empty required detail says, inside its field. */
const REQUIRED_MESSAGES: Record<string, string> = {
  'Stake name is required': 'Enter your stake’s name.', 'Ward name is required for a ward activity': 'Enter your ward or branch’s name.',
  'Event name is required': 'Enter the event’s name.', 'Event organizer name is required': 'Enter the organizer’s name.',
  'Event organizer phone number is required': 'Enter a phone number.',
};

/** Errors keyed to the controls the organizer needs to correct. */
export function eventInputErrors(input: Partial<EventInput>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const message of validateEventInput(input)) {
    let key = '';
    if (message.startsWith('Add the ward to your list')) key = 'wardEntry';
    else if (message.startsWith('Choose Stake')) key = 'level';
    else if (message.startsWith('Stake name') || message.includes('as a stake name')) key = 'stakeName';
    else if (message.startsWith('Ward name is')) key = 'wardName';
    else if (message.includes('ward names') || message.startsWith('Ward names') || message.startsWith('Each ward')) key = 'wardNames';
    else if (message.startsWith('Event name')) key = 'name';
    else if (message.startsWith('Description')) key = 'description';
    else if (message.startsWith('Event organizer name')) key = 'leaderName';
    else if (message.startsWith('Event organizer phone')) key = 'leaderPhone';
    else if (message.startsWith('Event organizer email')) key = 'leaderEmail';
    else if (message.startsWith('question ')) {
      const i = Number(message.match(/^question (\d+)/)?.[1]) - 1;
      key = `question.${input.fields?.[i]?.id ?? i}`;
      // Show the first problem; it is usually the one nearest the top of the question.
      if (result[key]) continue;
      const problem = message.replace(/^question \d+: /, '');
      result[key] = problem.charAt(0).toUpperCase() + problem.slice(1);
      continue;
    } else if (message === 'fields must be a list') key = 'questions';
    if (key) result[key] = REQUIRED_MESSAGES[message] ?? (message === 'Event organizer phone number is invalid' ? PHONE_ERROR : key === 'leaderEmail' ? input.leaderEmail?.trim() ? 'Enter a valid email address.' : 'Enter an email address.' : message === 'Event name is too long' ? 'Use 120 characters or fewer for the event name.' : message === 'Description is too long' ? 'Use 4,000 characters or fewer for the description.' : message);
  }
  if (!input.dates?.length) result.dates = 'Choose an activity date.';
  input.dates?.forEach((d, i) => {
    if (!d?.start) result[`date.${i}`] = 'Choose an activity date.';
    else if (!isValidDate(d.start)) result[`date.${i}`] = 'Enter a date like 6/12/2026, or dates like 6/12/2026 – 6/13/2026.';
    else if (d.end && (!isValidDate(d.end) || d.end < d.start)) result[`date.${i}`] = 'The end date must be on or after the start date.';
  });
  return result;
}

function validRating(value: Field['rating']): boolean { return !!value && Number.isInteger(value.max) && value.max >= 3 && value.max <= 10 && (value.icon === undefined || ['stars', 'hearts', 'thumbs_up'].includes(value.icon)); }

function validDestination(v: unknown): boolean { return v === 'next' || v === 'end' || (!!v && typeof v === 'object' && !Array.isArray(v) && typeof (v as { sectionId?: unknown }).sectionId === 'string' && !!(v as { sectionId: string }).sectionId && (v as { sectionId: string }).sectionId.length <= 10000); }

function validRouting(value: Field['routing']): boolean {

  return !!value && typeof value.enabled === 'boolean' && Array.isArray(value.targets) && value.targets.length <= 200 && value.targets.every(validDestination) && (value.other === undefined || validDestination(value.other));
}
